import 'angular';
import Features from '../feature-manager/Features';
import { RootScope } from '../types/angular-types';

class MessageItem {
    constructor(public message: string, public weight: number, public id: string) {
    }
}

class ProbablisticMessageProvider {
    private messageIdxs: number[];
    private messageList: MessageItem[];

    constructor(msgList: MessageItem[]) {
        this.messageIdxs = [];
        this.messageList = msgList;
        //Add the indexes to the messageIdxs list duplicating them as per the weight
        this.messageList.forEach((msgItem, idx) => {
            for (let i = 1; i <= msgItem.weight; i++) {
                this.messageIdxs.push(idx);
            }
        });
    }

    public isEmpty(): boolean {
        return !this.messageIdxs.length;
    }

    public getNextMessage(): MessageItem {
        if (this.isEmpty()) {
            return null;
        }

        var min = 0, max = this.messageIdxs.length; //max should be 1 more than no. of messages
        //The maximum is exclusive and the minimum is inclusive
        var randomMsgIdx = Math.floor(Math.random() * (max - min)) + min;
        const msgIdx = this.messageIdxs[randomMsgIdx];
        return this.messageList[msgIdx];
    }
}

interface DidYouKnowMessagesService {
    init: () => ng.IPromise<Object>;
    getAllMessages: () => ng.IPromise<ProbablisticMessageProvider>;
}

function DidYouKnowMessagesServiceFactory ($http: ng.IHttpService, AppConstants, EndpointConfig, $rootScope: RootScope, $q: ng.IQService, RegionService, localStorageService, $timeout: ng.ITimeoutService, FeatureManagerService) {
    const getMessagesList = (language: string): ng.IPromise<ProbablisticMessageProvider> => {
        const region = RegionService.getRegion(),
            stage = region ? EndpointConfig.stage(region) : null;
        const urlPrefix = AppConstants.DYKM_URL_PREFIX + (EndpointConfig.isPreviewSite() || stage === 'ALPHA' || stage === 'DEV' || stage === 'GAMMA' ? 'preview/' : 'prod/') + "messages_";
        return $q((resolve, reject) => {
            $http.get(urlPrefix + language + ".json").then((response) => {
                const data: any = response.data;
                if (data && data.messagesList) {
                    resolve(new ProbablisticMessageProvider(data.messagesList));
                } else {
                    reject(new ProbablisticMessageProvider([]));
                }
            }, (response) => {
                reject(new ProbablisticMessageProvider([]));
            });
        });
    };

    const service: DidYouKnowMessagesService = {
        init: () => {
            $rootScope.showDidYouKnow = false;
            $rootScope.didYouKnowMessage = '';
            $rootScope.didYouKnowMessageId = '';

            if (!FeatureManagerService.isFeatureEnabled(Features.UI.DidYouKnowMessages)) {
                //If the feature is not enabled, do not even make the call
                return $q.when({});
            }

            //This is a one time operation per application load
            var snoozeStartValue = localStorageService.get(AppConstants.DID_YOU_KNOW_SNOOZE_KEY);
            if (snoozeStartValue !== null) {
                //If the user had snoozed the DDYK tooltip in the past, we check if the time has pased.
                //If yes, we remove the key
                var now = (new Date()).getTime();
                if (snoozeStartValue + AppConstants.DID_YOU_KNOW_SNOOZE_TIME_MILLISEC < now) {
                    removeSnoozeKey();
                } else {
                    //If during app load the time has not passed, then schedule a removal after some time.
                    //This will make the snoozing work even if the app is not reloaded. This is important especially in cases where
                    //user does not reload page for many days
                    $timeout(removeSnoozeKey, AppConstants.DID_YOU_KNOW_SNOOZE_TIME_MILLISEC - (now - snoozeStartValue));
                }
            }
            var messageProvider: ProbablisticMessageProvider = null;
            $rootScope.showDidYouKnow = false;            
            $rootScope.$on("$routeChangeStart", getNextMessage);

            service.getAllMessages().then(function(msgProvider) {
                messageProvider = msgProvider;
                getNextMessage();
                isDidYouKnowMessageVisible();
                return msgProvider;
            });

            function getNextMessage() {
                if (messageProvider) {
                    const messageItem = messageProvider.getNextMessage();
                    $rootScope.didYouKnowMessage = messageItem.message;
                    $rootScope.didYouKnowMessageId = messageItem.id || '';
                }
            }

            function isDidYouKnowMessageVisible() {
                $rootScope.showDidYouKnow = !messageProvider.isEmpty() && !localStorageService.get(AppConstants.DID_YOU_KNOW_SNOOZE_KEY);
            }

            /**
             * This removes the snooze key for Did You Know feature
             */
            function removeSnoozeKey() {
                localStorageService.remove(AppConstants.DID_YOU_KNOW_SNOOZE_KEY);
                isDidYouKnowMessageVisible();
            }
        },
        getAllMessages: () => {
            const win = (window as any);
            const currentLanguage = win.determinePreferredLanguage().toLowerCase();
            return $q((resolve, reject) => {
                //First try to get language specific list, if that fails, get english messages     
                getMessagesList(currentLanguage).then(resolve, () => {
                    getMessagesList('en').then(resolve, reject);
                });
            });
        }
    };

    return service;
    
}

DidYouKnowMessagesServiceFactory.$inject = ['$http', 'AppConstants', 'EndpointConfig', '$rootScope', '$q', 'RegionService', 'localStorageService', '$timeout', 'FeatureManagerService'];

/**
 * This service will get messages from S3 bucket and show those on the website
 */
angular.module('GorillaBoy.Tutorial')
.factory('DidYouKnowMessagesService', DidYouKnowMessagesServiceFactory);

export {
    DidYouKnowMessagesService,
    ProbablisticMessageProvider
};
